import type React from "react";
import { useState } from "react";
import { ExternalLink, Pencil, Plus, Trash2, X, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { client as clientSchemas, type client, insights } from "@duress/shared";
import {
  useUpdateClientInfo,
  useUpdateClientIndustries,
  useUpdateClientCsm,
  useUsers,
  useClientContacts,
  useCreateContact,
  useUpdateContact,
  useDeleteContact,
} from "../useClients";

interface Props {
  client: client.ClientListItem;
}

// Every section's handleSave sends the *entire* client-info payload (the
// backend endpoint is a single bulk update, not per-field) -- this builds
// that payload from the current client plus whichever fields a section is
// actually changing, so each section only has to name the field(s) it owns.
function toUpdateRequest(
  c: client.ClientListItem,
  overrides: Partial<client.UpdateClientInfoRequest> = {},
): client.UpdateClientInfoRequest {
  return {
    region: c.region,
    pathfinderUsername: c.pathfinderUsername,
    googleDriveUrl: c.googleDriveUrl,
    canvaUrl: c.canvaUrl,
    contractEndDate: c.contractEndDate,
    falconCount: c.falconCount,
    eagleCount: c.eagleCount,
    appOnly: c.appOnly,
    tablets: c.tablets,
    simProvider: c.simProvider,
    customEscalation: c.customEscalation,
    receivesMonthlyReports: c.receivesMonthlyReports,
    arr: c.arr,
    ...overrides,
  };
}

const TRI_STATE_OPTIONS = [
  { label: "Yes", value: true },
  { label: "No", value: false },
  { label: "Not set", value: null },
] as const;

export function ClientDetailsTab({ client: c }: Props): React.JSX.Element {
  const [tab, setTab] = useState("account");

  return (
    <Tabs value={tab} onValueChange={setTab}>
      <TabsList className="border-b border-border mb-6 w-full">
        <TabsTrigger value="account">Account</TabsTrigger>
        <TabsTrigger value="setup">Setup</TabsTrigger>
        <TabsTrigger value="team">Team</TabsTrigger>
      </TabsList>
      <TabsContent value="account" className="max-w-2xl space-y-8">
        <RegionSection client={c} />
        <AccountSection client={c} />
        <ContractSection client={c} />
      </TabsContent>
      <TabsContent value="setup" className="max-w-2xl space-y-8">
        <HardwareSection client={c} />
        <CustomEscalationSection client={c} />
        <IndustriesSection client={c} />
      </TabsContent>
      <TabsContent value="team" className="max-w-2xl space-y-8">
        <CsmSection client={c} />
        <ContactsSection clientId={c.id} />
      </TabsContent>
    </Tabs>
  );
}

// ─── Region section ───────────────────────────────────────────────────────────

// A client's region scopes which peers it's benchmarked against on the
// Industry Insights page -- UK and AU clients never appear in the same
// industry row. See insights.service.ts.
// Region is a single tri-state-like field (just more than three options) with
// nothing else in the section -- there's no batch of fields to stage, so it
// auto-saves on click rather than going through an Edit/Save/Cancel cycle.
function RegionSection({ client: c }: { client: client.ClientListItem }): React.JSX.Element {
  const update = useUpdateClientInfo(c.id);

  return (
    <Section title="Region">
      <AutoSavePillField
        label="Region"
        value={c.region}
        options={clientSchemas.REGIONS.map((r) => ({ label: r, value: r }))}
        onSave={(region) => update.mutateAsync(toUpdateRequest(c, { region }))}
      />
    </Section>
  );
}

// ─── Account section ──────────────────────────────────────────────────────────

function AccountSection({ client: c }: { client: client.ClientListItem }): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [pathfinderUsername, setPathfinderUsername] = useState(c.pathfinderUsername ?? "");
  const [googleDriveUrl, setGoogleDriveUrl] = useState(c.googleDriveUrl ?? "");
  const [canvaUrl, setCanvaUrl] = useState(c.canvaUrl ?? "");
  const [error, setError] = useState<string | null>(null);

  const update = useUpdateClientInfo(c.id);

  const startEditing = (): void => {
    setPathfinderUsername(c.pathfinderUsername ?? "");
    setGoogleDriveUrl(c.googleDriveUrl ?? "");
    setCanvaUrl(c.canvaUrl ?? "");
    setError(null);
    setEditing(true);
  };

  const handleSave = async (): Promise<void> => {
    setError(null);
    try {
      await update.mutateAsync(
        toUpdateRequest(c, {
          pathfinderUsername: pathfinderUsername.trim() || null,
          googleDriveUrl: googleDriveUrl.trim() || null,
          canvaUrl: canvaUrl.trim() || null,
        }),
      );
      setEditing(false);
    } catch {
      setError("Couldn't save. Try again.");
    }
  };

  return (
    <Section title="Account" {...(!editing ? { onEdit: startEditing } : {})}>
      <div className="space-y-4">
        {editing ? (
          <>
            <Field label="Pathfinder username">
              <Input
                value={pathfinderUsername}
                onChange={(e) => setPathfinderUsername(e.target.value)}
                placeholder="e.g. acme_coffee"
              />
            </Field>
            <Field label="Google Drive folder">
              <Input
                type="url"
                value={googleDriveUrl}
                onChange={(e) => setGoogleDriveUrl(e.target.value)}
                placeholder="https://drive.google.com/…"
              />
            </Field>
            <Field label="Canva folder">
              <Input
                type="url"
                value={canvaUrl}
                onChange={(e) => setCanvaUrl(e.target.value)}
                placeholder="https://www.canva.com/…"
              />
            </Field>
          </>
        ) : (
          <dl className="space-y-3">
            <ReadRow label="Pathfinder username" value={c.pathfinderUsername} />
            <ReadRow label="Google Drive folder" value={c.googleDriveUrl} isLink />
            <ReadRow label="Canva folder" value={c.canvaUrl} isLink />
          </dl>
        )}

        {/* Always visible, independent of the Edit/Save/Cancel cycle above --
            it has its own auto-save, not a field staged for batch commit. */}
        <AutoSavePillField
          label="Receives monthly reports"
          value={c.receivesMonthlyReports}
          options={TRI_STATE_OPTIONS}
          onSave={(receivesMonthlyReports) =>
            update.mutateAsync(toUpdateRequest(c, { receivesMonthlyReports }))
          }
        />

        {editing ? (
          <>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            <div className="flex gap-2">
              <Button size="sm" onClick={() => void handleSave()} disabled={update.isPending}>
                {update.isPending ? "Saving…" : "Save"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(false)}
                disabled={update.isPending}
              >
                Cancel
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </Section>
  );
}

// ─── Contract Details section ─────────────────────────────────────────────────

function ContractSection({ client: c }: { client: client.ClientListItem }): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [arr, setArr] = useState<string>(c.arr?.toString() ?? "");
  const [contractEndDate, setContractEndDate] = useState(c.contractEndDate ?? "");
  const [error, setError] = useState<string | null>(null);

  const update = useUpdateClientInfo(c.id);

  const startEditing = (): void => {
    setArr(c.arr?.toString() ?? "");
    setContractEndDate(c.contractEndDate ?? "");
    setError(null);
    setEditing(true);
  };

  const handleSave = async (): Promise<void> => {
    setError(null);
    const parsedArr = arr.trim() === "" ? null : parseInt(arr.replace(/[^0-9]/g, ""), 10);
    if (arr.trim() !== "" && (parsedArr === null || isNaN(parsedArr))) {
      setError("ARR must be a whole dollar amount.");
      return;
    }
    try {
      await update.mutateAsync(
        toUpdateRequest(c, { contractEndDate: contractEndDate || null, arr: parsedArr }),
      );
      setEditing(false);
    } catch {
      setError("Couldn't save. Try again.");
    }
  };

  return (
    <Section title="Contract Details" {...(!editing ? { onEdit: startEditing } : {})}>
      {editing ? (
        <div className="space-y-4">
          <Field label="ARR (annual contract value)">
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
              <Input
                type="text"
                inputMode="numeric"
                value={arr}
                onChange={(e) => setArr(e.target.value)}
                placeholder="e.g. 120000"
                className="pl-7"
              />
            </div>
          </Field>
          <Field label="Contract end date">
            <Input
              type="date"
              value={contractEndDate}
              onChange={(e) => setContractEndDate(e.target.value)}
            />
          </Field>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void handleSave()} disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={update.isPending}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <dl className="space-y-3">
          <ReadRow label="ARR" value={c.arr != null ? formatARR(c.arr) : null} />
          <ReadRow
            label="Contract end date"
            value={c.contractEndDate ? formatDate(c.contractEndDate) : null}
          />
        </dl>
      )}
    </Section>
  );
}

// ─── Hardware section ─────────────────────────────────────────────────────────

const SIM_PROVIDER_OPTIONS = [
  { label: "Telstra", value: "Telstra" as const },
  { label: "Optus", value: "Optus" as const },
  { label: "Not set", value: null },
] as const;

function HardwareSection({ client: c }: { client: client.ClientListItem }): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [falconCount, setFalconCount] = useState<string>(c.falconCount?.toString() ?? "");
  const [eagleCount, setEagleCount] = useState<string>(c.eagleCount?.toString() ?? "");
  const [error, setError] = useState<string | null>(null);

  const update = useUpdateClientInfo(c.id);

  const startEditing = (): void => {
    setFalconCount(c.falconCount?.toString() ?? "");
    setEagleCount(c.eagleCount?.toString() ?? "");
    setError(null);
    setEditing(true);
  };

  const handleSave = async (): Promise<void> => {
    setError(null);
    const parsedFalcons = falconCount.trim() === "" ? null : parseInt(falconCount, 10);
    const parsedEagles = eagleCount.trim() === "" ? null : parseInt(eagleCount, 10);
    if (
      (falconCount.trim() !== "" && isNaN(parsedFalcons ?? NaN)) ||
      (eagleCount.trim() !== "" && isNaN(parsedEagles ?? NaN))
    ) {
      setError("Counts must be whole numbers.");
      return;
    }
    try {
      await update.mutateAsync(
        toUpdateRequest(c, { falconCount: parsedFalcons, eagleCount: parsedEagles }),
      );
      setEditing(false);
    } catch {
      setError("Couldn't save. Try again.");
    }
  };

  return (
    <Section title="Hardware & Setup" {...(!editing ? { onEdit: startEditing } : {})}>
      <div className="space-y-4">
        {editing ? (
          <div className="grid grid-cols-2 gap-4">
            <Field label="Falcons">
              <Input
                type="number"
                min={0}
                value={falconCount}
                onChange={(e) => setFalconCount(e.target.value)}
                placeholder="—"
              />
            </Field>
            <Field label="Eagles">
              <Input
                type="number"
                min={0}
                value={eagleCount}
                onChange={(e) => setEagleCount(e.target.value)}
                placeholder="—"
              />
            </Field>
          </div>
        ) : (
          <dl className="space-y-3">
            <ReadRow label="Falcons" value={c.falconCount?.toString() ?? null} />
            <ReadRow label="Eagles" value={c.eagleCount?.toString() ?? null} />
          </dl>
        )}

        {/* Always visible, independent of the Falcons/Eagles Edit/Save/Cancel
            above -- each of these auto-saves for itself. */}
        <AutoSavePillField
          label="App only"
          value={c.appOnly}
          options={TRI_STATE_OPTIONS}
          onSave={(appOnly) => update.mutateAsync(toUpdateRequest(c, { appOnly }))}
        />
        <AutoSavePillField
          label="Tablets"
          value={c.tablets}
          options={TRI_STATE_OPTIONS}
          onSave={(tablets) => update.mutateAsync(toUpdateRequest(c, { tablets }))}
        />
        <AutoSavePillField
          label="SIM provider"
          value={c.simProvider}
          options={SIM_PROVIDER_OPTIONS}
          onSave={(simProvider) => update.mutateAsync(toUpdateRequest(c, { simProvider }))}
        />

        {editing ? (
          <>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            <div className="flex gap-2">
              <Button size="sm" onClick={() => void handleSave()} disabled={update.isPending}>
                {update.isPending ? "Saving…" : "Save"}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setEditing(false)}
                disabled={update.isPending}
              >
                Cancel
              </Button>
            </div>
          </>
        ) : null}
      </div>
    </Section>
  );
}

// ─── Custom Escalation section ────────────────────────────────────────────────

function CustomEscalationSection({ client: c }: { client: client.ClientListItem }): React.JSX.Element {
  const update = useUpdateClientInfo(c.id);

  return (
    <Section title="Custom Escalation">
      <AutoSavePillField
        label="Custom escalation"
        value={c.customEscalation}
        options={TRI_STATE_OPTIONS}
        onSave={(customEscalation) => update.mutateAsync(toUpdateRequest(c, { customEscalation }))}
      />
    </Section>
  );
}

// ─── Industries section ───────────────────────────────────────────────────────

function IndustriesSection({ client: c }: { client: client.ClientListItem }): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<string[]>(c.industries);
  const [error, setError] = useState<string | null>(null);

  const update = useUpdateClientIndustries(c.id);

  const startEditing = (): void => {
    setSelected(c.industries);
    setError(null);
    setEditing(true);
  };

  const toggle = (industry: string): void => {
    setSelected((prev) =>
      prev.includes(industry) ? prev.filter((i) => i !== industry) : [...prev, industry],
    );
  };

  const handleSave = async (): Promise<void> => {
    setError(null);
    try {
      await update.mutateAsync(selected);
      setEditing(false);
    } catch {
      setError("Couldn't save. Try again.");
    }
  };

  return (
    <Section title="Industries" {...(!editing ? { onEdit: startEditing } : {})}>
      {editing ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {insights.INDUSTRIES.map((industry) => {
              const checked = selected.includes(industry);
              return (
                <label
                  key={industry}
                  className="flex cursor-pointer items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm transition-colors hover:bg-accent"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(industry)}
                    className="h-4 w-4 rounded border-input"
                  />
                  <span>{industry}</span>
                </label>
              );
            })}
          </div>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void handleSave()} disabled={update.isPending}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={update.isPending}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div>
          {c.industries.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {c.industries.map((ind) => (
                <span
                  key={ind}
                  className="rounded-full border border-border bg-accent px-2.5 py-0.5 text-xs font-medium text-foreground"
                >
                  {ind}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">—</p>
          )}
        </div>
      )}
    </Section>
  );
}

// ─── CSM section ─────────────────────────────────────────────────────────────

function CsmSection({ client: c }: { client: client.ClientListItem }): React.JSX.Element {
  const [editing, setEditing] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(c.csmUserId);
  const [error, setError] = useState<string | null>(null);

  const update = useUpdateClientCsm(c.id);
  const { data: users, isLoading: usersLoading } = useUsers();

  const startEditing = (): void => {
    setSelectedUserId(c.csmUserId);
    setError(null);
    setEditing(true);
  };

  const handleSave = async (): Promise<void> => {
    setError(null);
    try {
      await update.mutateAsync(selectedUserId);
      setEditing(false);
    } catch {
      setError("Couldn't save. Try again.");
    }
  };

  return (
    <Section title="CSM" {...(!editing ? { onEdit: startEditing } : {})}>
      {editing ? (
        <div className="space-y-4">
          <Field label="Assigned CSM">
            {usersLoading ? (
              <p className="text-sm text-muted-foreground">Loading users…</p>
            ) : (
              <Select
                value={selectedUserId ?? "none"}
                onChange={(e) => setSelectedUserId(e.target.value === "none" ? null : e.target.value)}
              >
                <option value="none">No CSM assigned</option>
                {users?.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <div className="flex gap-2">
            <Button size="sm" onClick={() => void handleSave()} disabled={update.isPending || usersLoading}>
              {update.isPending ? "Saving…" : "Save"}
            </Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} disabled={update.isPending}>
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <dl className="space-y-3">
          <ReadRow label="Assigned CSM" value={c.csmName} />
          {c.csmEmail ? (
            <ReadRow label="CSM email" value={c.csmEmail} />
          ) : null}
        </dl>
      )}
    </Section>
  );
}

// ─── Key Contacts section ─────────────────────────────────────────────────────

function ContactsSection({ clientId }: { clientId: string }): React.JSX.Element {
  const { data: contacts, isLoading } = useClientContacts(clientId);
  const createContact = useCreateContact(clientId);
  const updateContact = useUpdateContact(clientId);
  const deleteContact = useDeleteContact(clientId);

  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  return (
    <Section title="Key Contacts">
      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="space-y-2">
          {contacts && contacts.length > 0 ? (
            contacts.map((contact) =>
              editingId === contact.id ? (
                <ContactForm
                  key={contact.id}
                  initial={contact}
                  isPending={updateContact.isPending}
                  onSave={async (data) => {
                    await updateContact.mutateAsync(contact.id, data);
                    setEditingId(null);
                  }}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <ContactRow
                  key={contact.id}
                  contact={contact}
                  onEdit={() => setEditingId(contact.id)}
                  onDelete={() => void deleteContact.mutateAsync(contact.id)}
                />
              ),
            )
          ) : (
            <p className="text-sm text-muted-foreground">No contacts added yet.</p>
          )}

          {adding ? (
            <ContactForm
              isPending={createContact.isPending}
              onSave={async (data) => {
                await createContact.mutateAsync(data);
                setAdding(false);
              }}
              onCancel={() => setAdding(false)}
            />
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="mt-1 gap-1.5"
              onClick={() => setAdding(true)}
            >
              <Plus className="h-3.5 w-3.5" />
              Add contact
            </Button>
          )}
        </div>
      )}
    </Section>
  );
}

function ContactRow({
  contact,
  onEdit,
  onDelete,
}: {
  contact: client.ClientContact;
  onEdit: () => void;
  onDelete: () => void;
}): React.JSX.Element {
  return (
    <div className="flex items-start justify-between rounded-md border border-border bg-card px-4 py-3 gap-4">
      <div className="min-w-0 space-y-0.5">
        <p className="font-medium text-sm truncate">{contact.name}</p>
        {contact.role ? (
          <p className="text-xs text-muted-foreground truncate">{contact.role}</p>
        ) : null}
        <div className="flex flex-wrap gap-x-4 gap-y-0.5 mt-1">
          {contact.email ? (
            <a
              href={`mailto:${contact.email}`}
              className="text-xs text-primary hover:underline truncate"
            >
              {contact.email}
            </a>
          ) : null}
          {contact.phone ? (
            <a
              href={`tel:${contact.phone}`}
              className="text-xs text-muted-foreground hover:text-foreground truncate"
            >
              {contact.phone}
            </a>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <button
          type="button"
          onClick={onEdit}
          title="Edit contact"
          className="rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground transition-colors"
        >
          <Pencil className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={onDelete}
          title="Delete contact"
          className="rounded p-1.5 text-muted-foreground hover:bg-destructive/10 hover:text-destructive transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function ContactForm({
  initial,
  isPending,
  onSave,
  onCancel,
}: {
  initial?: client.ClientContact;
  isPending: boolean;
  onSave: (data: client.CreateClientContactRequest) => Promise<void>;
  onCancel: () => void;
}): React.JSX.Element {
  const [name, setName] = useState(initial?.name ?? "");
  const [role, setRole] = useState(initial?.role ?? "");
  const [phone, setPhone] = useState(initial?.phone ?? "");
  const [email, setEmail] = useState(initial?.email ?? "");
  const [error, setError] = useState<string | null>(null);

  const handleSave = async (): Promise<void> => {
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setError(null);
    try {
      await onSave({
        name: name.trim(),
        role: role.trim() || null,
        phone: phone.trim() || null,
        email: email.trim() || null,
      });
    } catch {
      setError("Couldn't save. Try again.");
    }
  };

  return (
    <div className="rounded-md border border-border bg-card px-4 py-3 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Name">
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Jane Smith"
            autoFocus
          />
        </Field>
        <Field label="Role">
          <Input
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="Operations Manager"
          />
        </Field>
        <Field label="Email">
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jane@example.com"
          />
        </Field>
        <Field label="Phone">
          <Input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+61 4xx xxx xxx"
          />
        </Field>
      </div>
      {error ? <p className="text-sm text-destructive">{error}</p> : null}
      <div className="flex gap-2">
        <Button size="sm" onClick={() => void handleSave()} disabled={isPending}>
          <Check className="h-3.5 w-3.5 mr-1" />
          {isPending ? "Saving…" : "Save"}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} disabled={isPending}>
          <X className="h-3.5 w-3.5 mr-1" />
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ─── Shared primitives ────────────────────────────────────────────────────────

function Section({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit?: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide">{title}</h2>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <Pencil className="h-3 w-3" />
            Edit
          </button>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}

// A pill picker that commits on click rather than staging into a Save/Cancel
// pair -- there's nothing to lose by picking the wrong one (just click
// again), so there's no batch to commit or discard. Wraps the in-flight
// value in an object rather than storing it bare, because T itself is
// commonly `boolean | null` (e.g. "Not set" IS null) -- a bare `T | null`
// couldn't tell "nothing pending" apart from "Not set is pending".
function AutoSavePillField<T>({
  label,
  value,
  options,
  onSave,
}: {
  label: string;
  value: T;
  options: readonly { label: string; value: T }[];
  onSave: (value: T) => Promise<unknown>;
}): React.JSX.Element {
  const [pending, setPending] = useState<{ value: T } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const displayValue = pending ? pending.value : value;

  const handleClick = (v: T): void => {
    if (pending || v === displayValue) return;
    setError(null);
    setPending({ value: v });
    onSave(v)
      .catch(() => setError("Couldn't save. Try again."))
      .finally(() => setPending(null));
  };

  return (
    <Field label={label}>
      <div className="flex flex-wrap items-center gap-2 mt-1">
        {options.map((opt) => {
          const active = displayValue === opt.value;
          const isSaving = pending !== null && pending.value === opt.value;
          return (
            <button
              key={opt.label}
              type="button"
              onClick={() => handleClick(opt.value)}
              disabled={pending !== null}
              className={`rounded-md border px-3 py-1.5 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                active
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-input bg-transparent hover:bg-accent"
              }`}
            >
              {isSaving ? "Saving…" : opt.label}
            </button>
          );
        })}
      </div>
      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}
    </Field>
  );
}

function ReadRow({
  label,
  value,
  isLink = false,
}: {
  label: string;
  value: string | null;
  isLink?: boolean;
}): React.JSX.Element {
  return (
    <div className="flex items-baseline gap-4">
      <dt className="text-xs text-muted-foreground w-36 shrink-0">{label}</dt>
      <dd className="text-sm min-w-0">
        {value ? (
          isLink ? (
            <a
              href={value}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-primary hover:underline truncate"
            >
              <span className="truncate">{value}</span>
              <ExternalLink className="h-3 w-3 shrink-0" />
            </a>
          ) : (
            <span>{value}</span>
          )
        ) : (
          <span className="text-muted-foreground">—</span>
        )}
      </dd>
    </div>
  );
}

export function formatARR(arr: number): string {
  return "$" + arr.toLocaleString("en-AU", { maximumFractionDigits: 0 });
}

function formatDate(iso: string): string {
  const [, m, d] = iso.split("-");
  const months = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
  ];
  return `${parseInt(d ?? "1", 10)} ${months[parseInt(m ?? "1", 10) - 1] ?? ""} ${iso.slice(0, 4)}`;
}
